// =============================================================================
// api/tours.ts
// =============================================================================

import type {
  Hotspot,
  UpdateTourPayload,
  VFloor,
  VHotspot,
  VRoom,
  VScene,
  VTour,
} from "@/types";
import { supabase } from "@cl/supabase";
import { track } from "@vercel/analytics";
import { scopeNormalAggregate } from "@u/demoScope";
import { handleSupabaseError } from "./_error";
import { mapHotspotRowToHotspot } from "./hotspots";
import { removeScanModel } from "./media";

//  ---------------
// FETCHS
//  ---------------
export const fetchUserTours = async (
  userId?: string,
  opts?: { ownId?: string },
): Promise<VTour[]> => {
  let query = supabase
    .from("v_tours")
    .select("*")
    .order("created_at", { ascending: false });

  if (userId) query = query.eq("user_id", userId);
  // Admin's aggregate view (no userId) sees every user's tours via RLS bypass.
  // Keep OTHER owners' demo/showcase tours out of it, but never hide the admin's
  // own (their showcase tours are demo too). Owners querying by their own userId
  // are unaffected. Scoping rule lives in demoScope (single source).
  else if (opts?.ownId)
    query = scopeNormalAggregate(query, "user_id", opts.ownId);

  const { data, error } = await query;
  if (error) handleSupabaseError(error, "fetchUserTours");
  return (data ?? []) as VTour[];
};

export const fetchPublicTours = async (): Promise<VTour[]> => {
  const { data, error } = await supabase
    .from("v_tours")
    .select("*")
    .eq("state", "published")
    .eq("visibility", "public")
    .order("created_at", { ascending: false });
  if (error) handleSupabaseError(error, "fetchPublicTours");
  return (data ?? []) as VTour[];
};

export const fetchPublicToursPreview = async (limit: number): Promise<VTour[]> => {
  const { data, error } = await supabase
    .from("v_tours")
    .select("*")
    .eq("state", "published")
    .eq("visibility", "public")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) handleSupabaseError(error, "fetchPublicToursPreview");
  return (data ?? []) as VTour[];
};

// Otros tours públicos del mismo owner — mini-portfolio del profile modal.
// Excluye el tour actual. `limit` acota el payload.
export const fetchOwnerPublicTours = async (
  userId: string,
  excludeTourId?: string,
  limit = 6,
): Promise<VTour[]> => {
  let query = supabase
    .from("v_tours")
    .select("*")
    .eq("user_id", userId)
    .eq("state", "published")
    .eq("visibility", "public")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (excludeTourId) query = query.neq("id", excludeTourId);

  const { data, error } = await query;
  if (error) handleSupabaseError(error, "fetchOwnerPublicTours");
  return (data ?? []) as VTour[];
};

export const fetchFeaturedTours = async (): Promise<VTour[]> => {
  const { data, error } = await supabase
    .from("v_tours")
    .select("*")
    .eq("state", "published")
    .eq("visibility", "public")
    .eq("is_featured", true)
    .order("created_at", { ascending: false })
    .limit(6);
  if (error) handleSupabaseError(error, "fetchFeaturedTours");
  return (data ?? []) as VTour[];
};

export const setTourFeatured = async (
  tourId: string,
  featured: boolean,
): Promise<void> => {
  const { error } = await supabase
    .from("tours")
    .update({ is_featured: featured })
    .eq("id", tourId);
  if (error) handleSupabaseError(error, "setTourFeatured");
};

export const setTourDemo = async (
  tourId: string,
  demo: boolean,
): Promise<void> => {
  const { error } = await supabase
    .from("tours")
    .update({ is_demo: demo })
    .eq("id", tourId);
  if (error) handleSupabaseError(error, "setTourDemo");
};

/** Reach quick-toggle: flip público ↔ no-listado (solo tours ya publicados). */
export const setTourVisibility = async (
  tourId: string,
  visibility: "public" | "unlisted",
): Promise<void> => {
  const { error } = await supabase
    .from("tours")
    .update({ visibility })
    .eq("id", tourId);
  if (error) handleSupabaseError(error, "setTourVisibility");
};

export const fetchTourById = async (id: string): Promise<VTour> => {
  const { data, error } = await supabase
    .from("v_tours")
    .select("*")
    .eq("id", id)
    .single();
  // PGRST116 = no row (missing / not accessible). Rethrow the ORIGINAL error so
  // its `.code` survives for the caller's not-found handling (usePublicTour's
  // retry-skip reads err.code); handleSupabaseError would wrap it in a plain
  // Error and also log an expected 404 as noise.
  if (error) {
    if (error.code === "PGRST116") throw error;
    handleSupabaseError(error, "fetchTourById");
  }
  return data as VTour;
};

// RLS handles authorization
export const fetchTourByShortId = async (shortId: string): Promise<VTour> => {
  const { data, error } = await supabase
    .from("v_tours")
    .select("*")
    .eq("short_id", shortId)
    .single();
  // PGRST116 = no row (missing / unpublished). Rethrow the ORIGINAL error so its
  // `.code` survives — usePublicTour's retry-skip checks err.code === "PGRST116"
  // to avoid a wasted retry, and this keeps expected 404s out of the console.
  if (error) {
    if (error.code === "PGRST116") throw error;
    handleSupabaseError(error, "fetchTourByShortId");
  }
  return data as VTour;
};

// ── Password-protected tours: server-enforced gate ──────────────────────────
// unlock_tour verifies the password server-side (rate-limited) and returns a
// short-lived grant token; tour_view_graph exchanges that token for the tour's
// full scene graph (bypasses the RLS that now hides protected tours from anon).

/** Verify a tour password server-side. Returns a grant token, or null if wrong. */
export const unlockTour = async (
  tourId: string,
  password: string,
): Promise<string | null> => {
  const { data, error } = await supabase.rpc("unlock_tour", {
    p_tour_id: tourId,
    p_password: password,
  });
  if (error) return null;
  return (data as string | null) ?? null;
};

export interface TourViewGraph {
  scenes: VScene[];
  floors: VFloor[];
  rooms: VRoom[];
  hotspots: Hotspot[];
}

/** Fetch a protected tour's scene graph with a valid grant token. */
export const fetchTourViewGraph = async (
  token: string,
): Promise<TourViewGraph | null> => {
  const { data, error } = await supabase.rpc("tour_view_graph", {
    p_token: token,
  });
  if (error || !data) return null;
  const raw = data as {
    scenes: VScene[];
    floors: VFloor[];
    rooms: VRoom[];
    hotspots: VHotspot[];
  };
  return {
    scenes: raw.scenes ?? [],
    floors: raw.floors ?? [],
    rooms: raw.rooms ?? [],
    hotspots: (raw.hotspots ?? []).map(mapHotspotRowToHotspot),
  };
};

//  ---------------
// CREATE
//  ---------------
export const createTour = async (
  title: string,
  userId: string,
): Promise<VTour> => {
  const { data: newRow, error: insertError } = await supabase
    .from("tours")
    .insert([{ title, user_id: userId, state: "draft", visibility: "private" }])
    .select("id")
    .single();

  if (insertError) handleSupabaseError(insertError, "createTour");

  track("tour_created", { tour_id: newRow!.id });
  return fetchTourById(newRow!.id);
};

//  ---------------
// DUPLICATE (deep clone via edge function)
//  ---------------
export const duplicateTour = async (
  tourId: string,
  suffix?: string,
): Promise<{ tourId: string }> => {
  const { data, error } = await supabase.functions.invoke("duplicate-tour", {
    body: { tourId, suffix },
  });
  if (error) handleSupabaseError(error, "duplicateTour");
  if (!data?.ok) throw new Error(data?.error ?? "duplicate_failed");

  track("tour_duplicated", { tour_id: data.tourId });
  return { tourId: data.tourId as string };
};

//  ---------------
// UPDATE
//  ---------------
export const updateTour = async (
  id: string,
  updates: UpdateTourPayload,
): Promise<void> => {
  const { error } = await supabase.from("tours").update(updates).eq("id", id);
  if (error) handleSupabaseError(error, "updateTour");
};

//  ---------------
// DELETE
//  ---------------
export const deleteTour = async (id: string): Promise<void> => {
  // The scan .glb has no media row (see uploadScanModel) → grab its path
  // before the row disappears so we can clean the storage object.
  const { data: pre } = await supabase
    .from("tours")
    .select("settings")
    .eq("id", id)
    .single();
  const scanPath = (pre?.settings as { scan?: { model_path?: string } } | null)
    ?.scan?.model_path;

  const { error } = await supabase.from("tours").delete().eq("id", id);
  if (error) handleSupabaseError(error, "deleteTour");

  if (scanPath) await removeScanModel(scanPath);
};

//  ---------------
// PASSWORD
//  ---------------
export const setTourPassword = async (
  tourId: string,
  password: string,
): Promise<void> => {
  const { error } = await supabase.rpc("set_tour_password", {
    p_tour_id: tourId,
    p_password: password,
  });
  if (error) handleSupabaseError(error, "setTourPassword");
};

export const clearTourPassword = async (tourId: string): Promise<void> => {
  const { error } = await supabase.rpc("clear_tour_password", {
    p_tour_id: tourId,
  });
  if (error) handleSupabaseError(error, "clearTourPassword");
};
/** Wipe a language's stored names across the tour (name + rooms/floors/hotspots).
 *  Used when an owner deletes a custom language. */
export const stripTourLanguage = async (
  tourId: string,
  lang: string,
): Promise<void> => {
  const { error } = await supabase.rpc("strip_tour_language", {
    p_tour_id: tourId,
    p_lang: lang,
  });
  if (error) handleSupabaseError(error, "stripTourLanguage");
};
